import { notify } from "@general-backoffice/core/index";
import type { ConnectorInfo, ConnectorTestReport, ConnectorType, NativeConnectorStatus, NativeIncident } from "@investhinks/api-client";
import moment from "moment";
import React, { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Alert, Badge, Button, Card, CardBody, CardHeader, Input, Spinner, Table } from "reactstrap";

import { connectorsEndpoints } from "../../api";

const CONNECTOR_LABEL: Record<ConnectorType, string> = { METAAPI: "MetaApi", MT5_NATIVE: "MT5 native" };

const fmt = (value: number | null | undefined, digits = 2): string =>
  value === null || value === undefined ? "n/a" : value.toFixed(digits);

const errorMessage = (e: unknown): string => {
  const response = (e as { response?: { data?: { error?: { message?: string } } } }).response;
  return response?.data?.error?.message ?? (e as Error).message ?? "Request failed";
};

export const TestReport: React.FC<{ report: ConnectorTestReport }> = ({ report }) => {
  const { summary, comparison } = report;
  return (
    <div className="mt-3">
      <div className="d-flex align-items-center mb-2">
        <Badge color={report.ok ? "success" : "danger"} className="mr-2">
          {report.ok ? "Passed" : "Failed"}
        </Badge>
        <small className="text-muted">
          {moment(report.createdAt).format("YYYY-MM-DD HH:mm:ss")} · admin {report.adminId}
        </small>
      </div>

      {!report.ok && (
        <Alert color="danger">
          <strong>{report.errorClass ?? "ERROR"}</strong>
          {report.errorMessage ? `: ${report.errorMessage}` : ""}
        </Alert>
      )}

      <Table size="sm" bordered responsive className="mb-3">
        <tbody>
          <tr>
            <th>Login route</th>
            <td>{summary.loginRoute ?? "n/a"}</td>
            <th>Server build</th>
            <td>{summary.serverBuild ?? "n/a"}</td>
            <th>Latency</th>
            <td>{summary.latencyMs === null ? "n/a" : `${summary.latencyMs} ms`}</td>
          </tr>
          {summary.account && (
            <>
              <tr>
                <th>Balance</th>
                <td>
                  {fmt(summary.account.balance)} {summary.account.currency}
                </td>
                <th>Equity</th>
                <td>{fmt(summary.account.equity)}</td>
                <th>Leverage</th>
                <td>1:{summary.account.leverage}</td>
              </tr>
              <tr>
                <th>Margin</th>
                <td>{fmt(summary.account.margin)}</td>
                <th>Free margin</th>
                <td>{fmt(summary.account.freeMargin)}</td>
                <th>Last deal</th>
                <td>
                  {summary.deals
                    ? `${summary.deals.lastDealTime ? moment(summary.deals.lastDealTime).format("YYYY-MM-DD HH:mm") : "none"} (${summary.deals.count} deals)`
                    : "n/a"}
                </td>
              </tr>
            </>
          )}
        </tbody>
      </Table>

      <h6>Open positions ({summary.positions.length})</h6>
      {summary.positions.length > 0 ? (
        <Table size="sm" responsive>
          <thead>
            <tr>
              <th>Ticket</th>
              <th>Symbol</th>
              <th>Side</th>
              <th>Volume</th>
              <th>Open price</th>
              <th>TP</th>
              <th>SL</th>
              <th>Quote (bid / ask)</th>
            </tr>
          </thead>
          <tbody>
            {summary.positions.map((position) => {
              const quote = summary.quotes.find((q) => q.symbol === position.symbol);
              return (
                <tr key={position.id}>
                  <td>{position.id}</td>
                  <td>{position.symbol}</td>
                  <td>{position.side}</td>
                  <td>{position.volume}</td>
                  <td>{position.openPrice}</td>
                  <td>{position.takeProfit ?? "-"}</td>
                  <td>{position.stopLoss ?? "-"}</td>
                  <td>{quote ? `${quote.bid} / ${quote.ask}` : "n/a"}</td>
                </tr>
              );
            })}
          </tbody>
        </Table>
      ) : (
        <p className="text-muted">No open positions.</p>
      )}

      <h6>Pending orders ({summary.pendingOrders.length})</h6>
      {summary.pendingOrders.length > 0 ? (
        <Table size="sm" responsive>
          <thead>
            <tr>
              <th>Ticket</th>
              <th>Symbol</th>
              <th>Type</th>
              <th>Volume</th>
              <th>Price</th>
            </tr>
          </thead>
          <tbody>
            {summary.pendingOrders.map((order) => (
              <tr key={order.ticket}>
                <td>{order.ticket}</td>
                <td>{order.symbol}</td>
                <td>{order.type}</td>
                <td>{order.volume}</td>
                <td>{order.price}</td>
              </tr>
            ))}
          </tbody>
        </Table>
      ) : (
        <p className="text-muted">No pending orders.</p>
      )}

      {comparison && (
        <>
          <h6>MetaApi comparison</h6>
          <Table size="sm" bordered responsive>
            <tbody>
              <tr>
                <th>MetaApi snapshot</th>
                <td>
                  {comparison.metaapi.snapshotAt ? moment(comparison.metaapi.snapshotAt).fromNow() : "none"} · equity{" "}
                  {fmt(comparison.metaapi.equity)} · {comparison.metaapi.openTrades ?? "n/a"} open
                </td>
                <th>Equity difference</th>
                <td>
                  <Badge color={comparison.equityMatches === null ? "secondary" : comparison.equityMatches ? "success" : "warning"}>
                    {fmt(comparison.equityDiff)} ({fmt(comparison.equityDiffPercent, 3)}%)
                  </Badge>
                </td>
              </tr>
              <tr>
                <th>Position set</th>
                <td colSpan={3}>
                  <Badge color={comparison.positionSetMatches ? "success" : "warning"}>
                    {comparison.positionSetMatches ? "match" : "differs"}
                  </Badge>{" "}
                  {comparison.positionsMatched} matched
                  {comparison.positionsOnlyNative.length > 0 && ` · only native: ${comparison.positionsOnlyNative.join(", ")}`}
                  {comparison.positionsOnlyMetaapi.length > 0 && ` · only MetaApi: ${comparison.positionsOnlyMetaapi.join(", ")}`}
                  {comparison.positionsMismatched.length > 0 && ` · differing: ${comparison.positionsMismatched.join(", ")}`}
                </td>
              </tr>
            </tbody>
          </Table>
        </>
      )}

      {summary.warnings.length > 0 && (
        <Alert color="warning">
          <strong>Warnings</strong>
          <ul className="mb-0">
            {summary.warnings.map((warning) => (
              <li key={warning}>{warning}</li>
            ))}
          </ul>
        </Alert>
      )}
    </div>
  );
};

/** Admin-only: current connector, mandatory read-only connection test, switch with reason, audit, fallback. */
const ConnectorPanel: React.FC<{ exchangeAccountId: number | string }> = ({ exchangeAccountId }) => {
  const [status, setStatus] = useState<NativeConnectorStatus | null>(null);
  const [info, setInfo] = useState<ConnectorInfo | null>(null);
  const [report, setReport] = useState<ConnectorTestReport | null>(null);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [testing, setTesting] = useState(false);
  const [saving, setSaving] = useState(false);
  const [target, setTarget] = useState<ConnectorType>("MT5_NATIVE");
  const [reason, setReason] = useState("");
  const [now, setNow] = useState(Date.now());
  const [incidents, setIncidents] = useState<NativeIncident[]>([]);

  const load = useCallback(async () => {
    try {
      const result = await connectorsEndpoints.getConnector(exchangeAccountId);
      setStatus(result.status);
      setInfo(result.info);
      setReport((current) => current ?? result.info.lastTest);
      setLoadError(null);
      connectorsEndpoints
        .listIncidents({ uid: result.info.uid })
        .then((list) => setIncidents(list.incidents))
        .catch(() => undefined);
    } catch (e) {
      setLoadError(errorMessage(e));
    }
  }, [exchangeAccountId]);

  useEffect(() => {
    void load();
  }, [load]);

  // offer the other connector by default
  const currentConnector = info?.connector;
  useEffect(() => {
    if (currentConnector) setTarget(currentConnector === "METAAPI" ? "MT5_NATIVE" : "METAAPI");
  }, [currentConnector]);

  // the switch closes by itself when the passing test turns 15 minutes old
  useEffect(() => {
    const timer = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(timer);
  }, []);

  if (loadError) return <Alert color="danger">Connector: {loadError}</Alert>;
  if (!info || !status) {
    return (
      <Card className="mb-4">
        <CardBody>
          <Spinner size="sm" /> Loading connector…
        </CardBody>
      </Card>
    );
  }

  const enabled = status.enabled;
  const ineligible = info.eligibility.eligible ? null : info.eligibility.reason;
  const testWindowOpen = !!info.switchAllowedUntil && new Date(info.switchAllowedUntil).getTime() > now;
  const liveOnServer = info.liveNativeOnServer ?? null;
  const secondsLeft = testWindowOpen ? Math.max(0, Math.floor((new Date(info.switchAllowedUntil!).getTime() - now) / 1000)) : 0;
  const toNative = target !== "METAAPI";
  const canApply =
    target !== info.connector &&
    reason.trim().length > 0 &&
    !saving &&
    (!toNative || (enabled && !ineligible && (testWindowOpen || !!liveOnServer)));

  const runTest = async (): Promise<void> => {
    setTesting(true);
    try {
      setReport(await connectorsEndpoints.testConnector(exchangeAccountId));
      await load();
    } catch (e) {
      notify({ type: "danger", message: errorMessage(e) });
    } finally {
      setTesting(false);
    }
  };

  const apply = async (): Promise<void> => {
    setSaving(true);
    try {
      await connectorsEndpoints.setConnector(exchangeAccountId, { connector: target, reason: reason.trim() });
      notify({ type: "success", message: `Connector set to ${CONNECTOR_LABEL[target]}` });
      setReason("");
      await load();
    } catch (e) {
      notify({ type: "danger", message: errorMessage(e) });
    } finally {
      setSaving(false);
    }
  };

  const clearFallback = async (): Promise<void> => {
    setSaving(true);
    try {
      await connectorsEndpoints.clearFallback(exchangeAccountId);
      notify({ type: "success", message: "Fallback cleared, native will resume" });
      await load();
    } catch (e) {
      notify({ type: "danger", message: errorMessage(e) });
    } finally {
      setSaving(false);
    }
  };

  const fallbackDetails = info.fallback?.details as
    | { rootCause?: { category: string; message: string; at: string; attempts?: number | null } }
    | null
    | undefined;
  const cause = fallbackDetails?.rootCause ?? null;
  const fallbackTestOk = !!info.fallback && !!info.lastTest?.ok && testWindowOpen && info.lastTest.createdAt >= info.fallback.since;

  return (
    <Card className="mb-4">
      <CardHeader className="d-flex align-items-center justify-content-between">
        <h5 className="mb-0">Connector</h5>
        <span>
          <Badge color={info.connector === "METAAPI" ? "secondary" : "primary"}>{CONNECTOR_LABEL[info.connector]}</Badge>
          {info.fallback && (
            <Badge color="danger" className="ml-2">
              served by MetaApi (fallback)
            </Badge>
          )}
        </span>
      </CardHeader>
      <CardBody>
        {!enabled && <Alert color="secondary">Native connector disabled on the controller. Test and native switch are unavailable.</Alert>}

        {info.fallback && (
          <Alert color="danger">
            <strong>Native fallback active</strong> since {moment(info.fallback.since).format("YYYY-MM-DD HH:mm")} ({info.fallback.source},{" "}
            {info.fallback.reason}). MetaApi is trading this account; the stored connector stays {CONNECTOR_LABEL[info.connector]}.
            {cause && (
              <div className="mt-2">
                <strong>Why:</strong> <Badge color="dark">{cause.category.replace(/_/g, " ").toLowerCase()}</Badge>{" "}
                <span title={cause.message}>
                  {moment(cause.at).format("HH:mm")} — {cause.message}
                  {cause.attempts ? ` (${cause.attempts} attempts)` : ""}
                </span>
              </div>
            )}
            {Array.isArray(info.fallback.unresolvedIntents) && info.fallback.unresolvedIntents.length > 0 && (
              <div>
                <strong>Unresolved write intents, manual review required:</strong> {(info.fallback.unresolvedIntents as string[]).join("; ")}
              </div>
            )}
            <div className="mt-2">
              <Button color="danger" size="sm" disabled={!fallbackTestOk || saving || !enabled} onClick={() => void clearFallback()}>
                Clear fallback and return to native
              </Button>{" "}
              <small>{fallbackTestOk ? `test valid for ${secondsLeft}s` : "needs a passing connection test taken after the failure"}</small>
            </div>
          </Alert>
        )}
        {info.awaitingMetaapiStop && (
          <Alert color="info">Fallback cleared. Waiting for the worker to confirm the MetaApi path stopped before MetaApi is undeployed.</Alert>
        )}
        {info.metaapiFallbackAccount.missing && (
          <Alert color="warning">
            MetaApi fallback account missing — will be recreated on fallback (checked{" "}
            {info.metaapiFallbackAccount.checkedAt ? moment(info.metaapiFallbackAccount.checkedAt).fromNow() : "never"}).
          </Alert>
        )}

        <div className="d-flex flex-wrap align-items-end">
          <div className="mr-3 mb-2" style={{ minWidth: 180 }}>
            <label className="form-control-label">Switch to</label>
            <Input
              type="select"
              value={target}
              disabled={(!!ineligible && info.connector === "METAAPI") || saving}
              onChange={(e) => setTarget(e.target.value as ConnectorType)}
            >
              <option value="METAAPI">MetaApi</option>
              <option value="MT5_NATIVE" disabled={!enabled}>
                MT5 native
              </option>
            </Input>
            {ineligible && <small className="text-danger">{ineligible}</small>}
          </div>
          <div className="mr-3 mb-2 flex-grow-1" style={{ minWidth: 220 }}>
            <label className="form-control-label">Reason</label>
            <Input value={reason} maxLength={1024} placeholder="Why is the connector changing?" onChange={(e) => setReason(e.target.value)} />
          </div>
          <div className="mb-2">
            <Button color="primary" disabled={!canApply} onClick={() => void apply()}>
              {saving ? <Spinner size="sm" /> : "Apply"}
            </Button>
          </div>
        </div>
        {toNative && !testWindowOpen && liveOnServer && (
          <small className="text-success">
            Switch allowed without a test: {liveOnServer.liveAccounts} account(s) on this server already run live on native (e.g.{" "}
            {liveOnServer.accountName}).
          </small>
        )}
        {toNative && !ineligible && enabled && !testWindowOpen && !liveOnServer && (
          <small className="text-muted">Run a passing connection test first (on this or any account of the same server); it unlocks the switch for the whole server for 15 minutes.</small>
        )}
        {toNative && testWindowOpen && (
          <small className="text-success">
            Switch allowed until {moment(info.switchAllowedUntil!).format("HH:mm")} via test on{" "}
            {info.switchAllowedBy?.own ? "this account" : (info.switchAllowedBy?.accountName ?? "another account on this server")} ({secondsLeft}s left,
            valid for every eligible account on this server).
          </small>
        )}

        <hr />
        <div className="d-flex align-items-center">
          <Button color="info" outline disabled={!enabled || !!ineligible || testing} onClick={() => void runTest()}>
            {testing ? <Spinner size="sm" /> : "Test connection"}
          </Button>
          <small className="text-muted ml-3">
            Read-only native session with the stored credentials (separate from the bot, never trades).
            {enabled ? ` ${status.capableWorkers} capable worker(s).` : ""}
          </small>
        </div>
        {report && <TestReport report={report} />}

        {incidents.length > 0 && (
          <>
            <hr />
            <h6>Native incidents</h6>
            <Table size="sm" responsive>
              <thead>
                <tr>
                  <th>When</th>
                  <th>Severity</th>
                  <th>Category</th>
                  <th>Message</th>
                </tr>
              </thead>
              <tbody>
                {incidents.slice(0, 20).map((incident) => (
                  <tr key={incident.id}>
                    <td className="text-nowrap">{moment(incident.createdAt).format("MM-DD HH:mm:ss")}</td>
                    <td>
                      <Badge color={incident.severity === "WARNING" ? "warning" : "danger"}>{incident.severity}</Badge>
                    </td>
                    <td>{incident.category.replace(/_/g, " ").toLowerCase()}</td>
                    <td title={JSON.stringify(incident.details)}>{incident.message}</td>
                  </tr>
                ))}
              </tbody>
            </Table>
            <Link to="/connectors">All incidents</Link>
          </>
        )}

        {info.audits.length > 0 && (
          <>
            <hr />
            <h6>History</h6>
            <Table size="sm" responsive>
              <thead>
                <tr>
                  <th>When</th>
                  <th>Change</th>
                  <th>Admin</th>
                  <th>Reason</th>
                </tr>
              </thead>
              <tbody>
                {info.audits.map((audit) => (
                  <tr key={audit.id}>
                    <td>{moment(audit.createdAt).format("YYYY-MM-DD HH:mm")}</td>
                    <td>
                      {CONNECTOR_LABEL[audit.from]} → {CONNECTOR_LABEL[audit.to]}
                    </td>
                    <td>{audit.adminId}</td>
                    <td>{audit.reason}</td>
                  </tr>
                ))}
              </tbody>
            </Table>
          </>
        )}
      </CardBody>
    </Card>
  );
};

export default ConnectorPanel;
